import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, CheckCircle2, Download, ArrowRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export function LeadCaptureModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    work_email: '',
    company: '',
    project_type: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.work_email.trim()) {
      toast.error('Please provide your name and work email');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.work_email)) {
      toast.error('Please provide a valid email address');
      return;
    }

    setSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/lead-capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to capture lead');
      }

      const data = await response.json();
      setSubmitted(true);
      toast.success('Thank you! Starting download...');
      
      // Trigger download
      setTimeout(() => {
        const link = document.createElement('a');
        link.href = '/assets/BAKLIGHT_Mexico_Production_Guide_2026.pdf';
        link.download = 'BAKLIGHT_Mexico_Production_Guide_2026.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, 500);

      // Show success and then show follow-up modal
      setTimeout(() => {
        setSubmitted(false);
        onSuccess(); // Trigger follow-up modal
        onClose();
        // Reset form
        setFormData({
          name: '',
          work_email: '',
          company: '',
          project_type: ''
        });
      }, 2000);
      
    } catch (err) {
      toast.error('Something went wrong. Please try again or email us at paco@baklight.com');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" 
      onClick={onClose}
      data-testid="lead-capture-modal-overlay"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg p-6 sm:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        data-testid="lead-capture-modal"
      >
        {submitted ? (
          <div className="text-center py-12">
            <Download size={48} className="text-[hsl(var(--bk-accent))] mx-auto mb-4" />
            <h3 className="font-display text-2xl text-[hsl(var(--bk-fg))] mb-2">Download Starting...</h3>
            <p className="font-body text-[hsl(var(--bk-fg-muted))]">Thank you for your interest!</p>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="font-display text-2xl text-[hsl(var(--bk-fg))] mb-2">
                  Download Mexico Production Guide
                </h3>
                <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))]">
                  Get instant access to incentives, locations, and production resources
                </p>
              </div>
              <button 
                onClick={onClose} 
                className="text-[hsl(var(--bk-fg-muted))] hover:text-[hsl(var(--bk-fg))] transition-colors"
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] mb-2">
                  Name *
                </Label>
                <Input
                  data-testid="lead-capture-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Your name"
                  className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] placeholder:text-[hsl(var(--bk-fg-subtle))]"
                />
              </div>

              <div>
                <Label className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] mb-2">
                  Work Email *
                </Label>
                <Input
                  data-testid="lead-capture-email-input"
                  type="email"
                  value={formData.work_email}
                  onChange={(e) => setFormData({...formData, work_email: e.target.value})}
                  placeholder="your@company.com"
                  className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] placeholder:text-[hsl(var(--bk-fg-subtle))]"
                />
              </div>

              <div>
                <Label className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] mb-2">
                  Company
                </Label>
                <Input
                  data-testid="lead-capture-company-input"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  placeholder="Your company (optional)"
                  className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] placeholder:text-[hsl(var(--bk-fg-subtle))]"
                />
              </div>

              <div>
                <Label className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] mb-2">
                  Project Type
                </Label>
                <Select 
                  value={formData.project_type} 
                  onValueChange={(v) => setFormData({...formData, project_type: v})}
                >
                  <SelectTrigger 
                    className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                    data-testid="lead-capture-project-type-select"
                  >
                    <SelectValue placeholder="Select type (optional)" />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] z-[110]">
                    <SelectItem value="commercial">Commercial / Advertising</SelectItem>
                    <SelectItem value="documentary">Documentary</SelectItem>
                    <SelectItem value="fiction">Fiction / Feature</SelectItem>
                    <SelectItem value="series">Series / Episodic</SelectItem>
                    <SelectItem value="animation">Animation / VFX</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full font-body text-sm px-6 py-3 bg-[hsl(var(--bk-accent))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="lead-capture-submit-btn"
              >
                {submitting ? 'Processing...' : 'Download Guide'} <Download size={16} />
              </button>

              <p className="text-xs text-center text-[hsl(var(--bk-fg-subtle))] mt-4">
                Your information will be kept confidential
              </p>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}

export function FollowUpModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[101] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" 
      onClick={onClose}
      data-testid="follow-up-modal-overlay"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-accent))]/30 rounded-lg p-6 sm:p-8 max-w-md w-full"
        data-testid="follow-up-modal"
      >
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[hsl(var(--bk-accent))]/10 flex items-center justify-center">
            <CheckCircle2 size={32} className="text-[hsl(var(--bk-accent))]" />
          </div>
          
          <h3 className="font-display text-2xl text-[hsl(var(--bk-fg))] mb-3">
            Planning a production in Mexico?
          </h3>
          
          <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] mb-6 leading-relaxed">
            Let us provide you with a detailed estimate and production plan tailored to your project.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onClose}
              className="flex-1 font-body text-sm px-6 py-3 border border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] rounded-[var(--radius)] hover:bg-[hsl(var(--bk-bg))] transition-colors"
            >
              Maybe Later
            </button>
            <a
              href="/contact"
              className="flex-1 font-body text-sm px-6 py-3 bg-[hsl(var(--bk-accent))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              data-testid="follow-up-contact-link"
            >
              Request an Estimate <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
